import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import {
  Search,
  User,
  Briefcase,
  MapPin,
  MessageSquare,
  Shield,
  Building2,
  ChevronRight,
  Filter,
  X,
  ChevronDown,
  Clock
} from "lucide-react";

const UserSearch = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [skillsFilter, setSkillsFilter] = useState("");
  const [industryFilter, setIndustryFilter] = useState("");
  const [experienceMin, setExperienceMin] = useState("");
  const [experienceMax, setExperienceMax] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const searchUsers = async (append = false) => {
    setLoading(true);
    setHasSearched(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append("q", searchQuery);
      if (roleFilter) params.append("role", roleFilter);
      if (locationFilter) params.append("location", locationFilter);
      if (skillsFilter) params.append("skills", skillsFilter);
      if (industryFilter) params.append("industry", industryFilter);
      if (experienceMin) params.append("experience_min", experienceMin);
      if (experienceMax) params.append("experience_max", experienceMax);
      if (append) params.append("skip", results.length.toString());
      
      const res = await fetch(`${API}/users/search?${params}`, {
        credentials: "include"
      });
      
      if (res.ok) {
        const data = await res.json();
        if (append) {
          setResults(prev => [...prev, ...(data.users || [])]);
        } else {
          setResults(data.users || []);
        }
        setHasMore(data.has_more || false);
      } else {
        toast.error("Failed to search users");
      }
    } catch (error) {
      toast.error("Failed to search users");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      searchUsers();
    }
  };

  const clearFilters = () => {
    setRoleFilter("");
    setLocationFilter("");
    setSkillsFilter("");
    setIndustryFilter("");
    setExperienceMin("");
    setExperienceMax("");
  };

  const hasActiveFilters = roleFilter || locationFilter || skillsFilter || industryFilter || experienceMin || experienceMax;

  return (
    <div className="min-h-screen bg-obsidian flex" data-testid="user-search-page">
      <Sidebar />
      
      <main className="flex-1 lg:ml-64 p-4 sm:p-6 pt-20 lg:pt-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">
              FIND <span className="text-crimson">PEOPLE</span>
            </h1>
            <p className="text-gray-400 text-sm sm:text-base">Search and connect with recruiters, job seekers, and professionals</p>
          </div>
          
          {/* Search Box */}
          <div className="bg-obsidian-light border border-[#222] p-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="pl-10 bg-obsidian border-[#333] text-white"
                  data-testid="user-search-input"
                />
              </div>
              <Button
                onClick={() => searchUsers()}
                disabled={loading}
                className="bg-crimson hover:bg-crimson/90"
                data-testid="search-button"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Search
                  </>
                )}
              </Button>
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant="outline"
                className={`border-[#333] ${hasActiveFilters ? 'text-crimson border-crimson' : 'text-white'}`}
                data-testid="toggle-filters-button"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
                {hasActiveFilters && <span className="ml-1 w-2 h-2 bg-crimson rounded-full" />}
              </Button>
            </div>

            {/* Expandable Filters */}
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="border-t border-[#333] pt-4"
              >
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm text-gray-400">Filter Options</span>
                  {hasActiveFilters && (
                    <button 
                      onClick={clearFilters}
                      className="text-xs text-crimson hover:underline flex items-center gap-1"
                    >
                      <X className="w-3 h-3" /> Clear All
                    </button>
                  )}
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {/* Role Filter */}
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">User Type</label>
                    <select
                      value={roleFilter}
                      onChange={(e) => setRoleFilter(e.target.value)}
                      className="w-full px-3 py-2 bg-obsidian border border-[#333] text-white text-sm"
                      data-testid="filter-role"
                    >
                      <option value="">All Users</option>
                      <option value="jobseeker">Job Seekers</option>
                      <option value="recruiter">Recruiters</option>
                    </select>
                  </div>

                  {/* Location Filter */}
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Location</label>
                    <Input
                      placeholder="e.g., New York, Remote"
                      value={locationFilter}
                      onChange={(e) => setLocationFilter(e.target.value)}
                      className="bg-obsidian border-[#333] text-white text-sm"
                      data-testid="filter-location"
                    />
                  </div>

                  {/* Skills Filter (for job seekers) */}
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Skills (comma-separated)</label>
                    <Input
                      placeholder="e.g., React, Python, Design"
                      value={skillsFilter}
                      onChange={(e) => setSkillsFilter(e.target.value)}
                      className="bg-obsidian border-[#333] text-white text-sm"
                      data-testid="filter-skills"
                    />
                  </div>

                  {/* Industry Filter (for recruiters) */}
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Industry</label>
                    <Input
                      placeholder="e.g., Technology, Healthcare"
                      value={industryFilter}
                      onChange={(e) => setIndustryFilter(e.target.value)}
                      className="bg-obsidian border-[#333] text-white text-sm"
                      data-testid="filter-industry"
                    />
                  </div>

                  {/* Experience Range */}
                  <div className="sm:col-span-2 lg:col-span-2">
                    <label className="text-xs text-gray-500 mb-1 block">Years of Experience</label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        placeholder="Min"
                        value={experienceMin}
                        onChange={(e) => setExperienceMin(e.target.value)}
                        min="0"
                        className="bg-obsidian border-[#333] text-white text-sm w-24"
                        data-testid="filter-exp-min"
                      />
                      <span className="text-gray-500">to</span>
                      <Input
                        type="number"
                        placeholder="Max"
                        value={experienceMax}
                        onChange={(e) => setExperienceMax(e.target.value)}
                        min="0"
                        className="bg-obsidian border-[#333] text-white text-sm w-24"
                        data-testid="filter-exp-max"
                      />
                      <span className="text-gray-500 text-sm">years</span>
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex justify-end">
                  <Button
                    onClick={() => searchUsers()}
                    disabled={loading}
                    className="bg-crimson hover:bg-crimson/90"
                  >
                    Apply Filters
                  </Button>
                </div>
              </motion.div>
            )}
          </div>

          {/* Results */}
          {hasSearched && (
            <div className="space-y-4">
              {results.length === 0 && !loading ? (
                <div className="text-center py-12 bg-obsidian-light border border-[#222]">
                  <User className="w-12 h-12 mx-auto mb-4 text-gray-600" />
                  <p className="text-gray-400">No users found</p>
                  <p className="text-sm text-gray-500 mt-2">Try different keywords or adjust your filters</p>
                </div>
              ) : (
                <>
                  <p className="text-gray-400 text-sm">
                    Found {results.length} user{results.length !== 1 ? "s" : ""}
                    {hasActiveFilters && " (filtered)"}
                  </p>
                  
                  {results.map((result, i) => (
                    <motion.div
                      key={result.user_id}
                      className="bg-obsidian-light border border-[#222] p-4 hover:border-crimson/30 transition-colors cursor-pointer"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.03 }}
                      onClick={() => navigate(`/profile/${result.user_id}`)}
                      data-testid={`user-result-${result.user_id}`}
                    >
                      <div className="flex items-center gap-4">
                        {/* Avatar */}
                        <div className="w-14 h-14 bg-crimson/20 flex items-center justify-center flex-shrink-0">
                          {result.picture ? (
                            <img 
                              src={result.picture} 
                              alt={result.name} 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <User className="w-7 h-7 text-crimson" />
                          )}
                        </div>
                        
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-heading font-bold text-white truncate">
                              {result.name || "Unnamed User"}
                            </h3>
                            <span className={`px-2 py-0.5 text-xs uppercase ${
                              result.role === "recruiter" 
                                ? "bg-blue-500/20 text-blue-400"
                                : "bg-green-500/20 text-green-400"
                            }`}>
                              {result.role}
                            </span>
                            {result.privacy_settings?.message_approval === "approval_required" && (
                              <Shield className="w-4 h-4 text-yellow-400" title="Requires message approval" />
                            )}
                          </div>
                          
                          {result.headline && (
                            <p className="text-gray-400 text-sm truncate">{result.headline}</p>
                          )}
                          
                          <div className="flex items-center gap-4 mt-1 text-xs text-gray-500 flex-wrap">
                            {result.location && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {result.location}
                              </span>
                            )}
                            {result.industry && (
                              <span className="flex items-center gap-1">
                                <Building2 className="w-3 h-3" />
                                {result.industry}
                              </span>
                            )}
                            {result.years_experience !== undefined && result.years_experience !== null && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {result.years_experience} years exp.
                              </span>
                            )}
                          </div>
                          
                          {result.skills && result.skills.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {result.skills.slice(0, 4).map(skill => (
                                <span key={skill} className="px-2 py-0.5 bg-obsidian text-gray-400 text-xs border border-[#333]">
                                  {skill}
                                </span>
                              ))}
                              {result.skills.length > 4 && (
                                <span className="text-xs text-gray-500">+{result.skills.length - 4} more</span>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Arrow */}
                        <ChevronRight className="w-5 h-5 text-gray-600 flex-shrink-0" />
                      </div>
                    </motion.div>
                  ))}

                  {/* Load More */}
                  {hasMore && (
                    <div className="text-center pt-4">
                      <Button
                        onClick={() => searchUsers(true)}
                        disabled={loading}
                        variant="outline"
                        className="border-[#333] text-white"
                      >
                        {loading ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        ) : (
                          <ChevronDown className="w-4 h-4 mr-2" />
                        )}
                        Load More
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {!hasSearched && (
            <div className="text-center py-12 bg-obsidian-light border border-[#222]">
              <Search className="w-12 h-12 mx-auto mb-4 text-gray-600" />
              <p className="text-gray-400">Search for users by name, email, or use filters</p>
              <p className="text-sm text-gray-500 mt-2">Find recruiters, job seekers, or anyone on the platform</p>
              <Button
                onClick={() => searchUsers()}
                className="mt-4 bg-crimson hover:bg-crimson/90"
              >
                Browse All Users
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default UserSearch;
